import React, { useState, useEffect } from 'react';
import { cloneDeep } from 'lodash';

import { Modal, notification, Form, Checkbox, Input, Button } from 'antd';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import { MenuOutlined, ReloadOutlined } from '@ant-design/icons';

import './styles.less';
import { pipelinePagesTypes } from '../../constants/enums/pipelinePagesTypes';

const PipelineOverviewConfigurator = ({
  rootContainer,
  closeModal,
  columns,
  updateColumnsState,
  saveControlConfiguration,
  refreshConstrolConfiguration,
  serviceRequestType,
}) => {
  const [columnsConfigForm] = Form.useForm();
  const [columnsArray, setColumnsArray] = useState(null);
  const [checkedConfigs, setCheckedConfigs] = useState(null);
  const [columnName, setColumnName] = useState('column');
  
  useEffect(() => {
    if (!!columns) {
      const updatedArray = [];

      Object.keys(columns).forEach((key) => {
        updatedArray.push({
          id: key,
          ...columns[key],
        });
      });

      const visibleCols = updatedArray.filter((v) => v.visible).map((obj) => obj.id);
      if (visibleCols?.length) {
        setCheckedConfigs(visibleCols);
      }

      setColumnsArray(updatedArray);
    } else {
      setColumnsArray(null);
    }
  }, [columns]);

  useEffect(() => {
    const entryName = refreshConstrolConfiguration
    ? serviceRequestType === pipelinePagesTypes.ServiceRequestsCustomTypesOverview
      ? 'custom type'
      : 'stage'
    : 'column';

    setColumnName(entryName)
  }, [serviceRequestType]);

  const handleSaveConfigs = () => {
    const formData = columnsConfigForm.getFieldsValue();

    const columnsArray =
      (columns &&
        Object.values(columns)
          .map((c) => {
            if (serviceRequestType === pipelinePagesTypes.ServiceRequestsOverview) {
            }

            switch (serviceRequestType) {
              case pipelinePagesTypes.ServiceRequestsCustomTypesOverview:
                return {
                  id: c.customType,
                  visible: formData[`${c.customType}`].visible,
                  name: c.name,
                  order: formData[`${c.customType}`].order,
                };
              case pipelinePagesTypes.ServiceRequestsStagesOverview:
                return {
                  id: c.stage,
                  visible: formData[`${c.stage}`].visible,
                  name: c.name,
                  order: formData[`${c.stage}`].order,
                };
              case pipelinePagesTypes.ServiceRequestsOverview:
              default:
                return {
                  id: c.status,
                  visible: formData[`${c.status}`].visible,
                  name: c.name,
                  order: formData[`${c.status}`].order,
                };
            }
          })
          .sort((a, b) => a.order - b.order)) ||
      [];

    const updatedColumnsConfigs = {};

    columnsArray.forEach((c) => {
      updatedColumnsConfigs[c.id] = columns[c.id];

      updatedColumnsConfigs[c.id].id = c.id;
      updatedColumnsConfigs[c.id].visible = formData[`${c.id}`].visible;
      updatedColumnsConfigs[c.id].name = c.name;
      updatedColumnsConfigs[c.id].order = formData[`${c.id}`].order;
    });

    updateColumnsState(updatedColumnsConfigs);

    if (typeof saveControlConfiguration === 'function') {
      saveControlConfiguration(columnsArray);
    }

    closeModal();
  };

  const layout = {
    labelCol: { span: 10 },
    wrapperCol: { span: 14 },
  };

  const onDragEnd = (result, columnsArray, configsForm) => {
    if (!result.destination) return;
    const { source, destination } = result;

    const fromIndex = source.index;
    const toIndex = destination.index;

    if (source.index !== destination.index) {
      const updatedColumns = cloneDeep(columnsArray);

      const [removed] = updatedColumns.splice(fromIndex, 1);
      updatedColumns.splice(toIndex, 0, removed);

      updatedColumns.forEach((col, index) => {
        col.order = index + 1;

        configsForm.setFieldsValue({
          [col.id]: {
            order: col.order,
          },
        });
      });

      setColumnsArray(updatedColumns);
    }
  };

  const refresh = () => {
    if (typeof refreshConstrolConfiguration === 'function') {
      refreshConstrolConfiguration();
    }
  };

  const onChangeForm = (changedValues, allValues) => {
    if (refreshConstrolConfiguration && allValues) {
      let visibleCount = 0;
      const checkedVals = [];

      for (const [key, value] of Object.entries(allValues)) {
        if (value.visible) {
          visibleCount++;
          checkedVals.push(key);
        }
      }

      setCheckedConfigs(checkedVals);

      if (visibleCount < 1) {
        notification.info({
          message: 'Info',
          top: 55,
          duration: 5,
          getContainer: rootContainer,
          description: `At least 1 ${columnName} must be selected`,
        });
      } else if (visibleCount >= 6) {
        notification.info({
          message: 'Info',
          top: 55,
          duration: 5,
          getContainer: rootContainer,
          description: `Maximum 6 ${columnName}s can be selected at a time`,
        });
      }
    } else {
      setCheckedConfigs(null);
    }
  };

  return (
    <>
      <Modal
        width={700}
        title={
          <>
            <span>Pipeline Overview Configurator</span>
            {refreshConstrolConfiguration ? (
              <span style={{ float: 'right', marginRight: '30px', marginTop: '-4px' }}>
                <Button type="link" title={`Refresh ${columnName}s`} icon={<ReloadOutlined />} onClick={() => refresh()} />
              </span>
            ) : null}
          </>
        }
        open={true}
        onCancel={closeModal}
        cancelText={'Close'}
        onOk={handleSaveConfigs}
        okText={'Save'}
        okType="primary"
        getContainer={rootContainer}
        className="table-config-modal"
        id="table-config-modal"
      >
        <Form
          {...layout}
          form={columnsConfigForm}
          onValuesChange={(changedValues, allValues) => onChangeForm(changedValues, allValues)}
          size="small"
          layout="horizontal"
          className="table-configuration-form"
          id="table-configuration-form"
        >
          <DragDropContext onDragEnd={(result) => onDragEnd(result, columnsArray, columnsConfigForm)}>
            <Droppable droppableId="columns">
              {(provided, snapshot) => {
                return (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {columnsArray && columnsArray.length
                      ? columnsArray
                          .sort((a, b) => a.order - b.order)
                          .map((col, index) => {
                            return (
                              <Draggable key={col.id} draggableId={col.id} index={index}>
                                {(provided, snapshot) => {
                                  return (
                                    <div
                                      key={col.id}
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      style={{
                                        userSelect: 'none',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        ...provided.draggableProps.style,
                                      }}
                                    >
                                      <Form.Item noStyle key={col.id}>
                                        <div style={{ display: 'flex', alignItems: 'baseline', paddingBottom: '10px' }}>
                                          <Form.Item
                                            title="Click to show/hide bucket"
                                            name={[col.id, 'visible']}
                                            noStyle
                                            initialValue={col.visible}
                                            valuePropName="checked"
                                          >
                                            <Checkbox
                                              disabled={
                                                refreshConstrolConfiguration && checkedConfigs?.length >= 6 && !checkedConfigs.includes(col.id)
                                              }
                                            />
                                          </Form.Item>
                                          <span style={{ marginLeft: '10px' }}>{col.name}</span>
                                        </div>
                                        <Form.Item name={[col.id, 'name']} initialValue={col.name} className="hide">
                                          <Input />
                                        </Form.Item>
                                        <Form.Item name={[col.id, 'order']} initialValue={col.order} className="hide">
                                          <Input />
                                        </Form.Item>
                                      </Form.Item>{' '}
                                      <span style={{ marginLeft: '10px', cursor: 'grab' }}>
                                        <MenuOutlined />
                                      </span>
                                    </div>
                                  );
                                }}
                              </Draggable>
                            );
                          })
                      : null}

                    {provided.placeholder}
                  </div>
                );
              }}
            </Droppable>
          </DragDropContext>
        </Form>
      </Modal>
    </>
  );
};

export default PipelineOverviewConfigurator;
